import { useEffect, useMemo, useState } from 'react'
import './OrderInfo.css'
import { productTypes, securityRequirementOptions } from 'src/lists'
import { DatePicker } from '@impres-development/assets/client'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DeleteOutline, ExpandMore } from '@mui/icons-material'
import dayjs, { Dayjs } from 'dayjs'
import { confidenceType, portalOrderType, shipToType } from './orderTypes'
import { fieldLengths } from './fieldLengths'
import { usePortalDivisionList, usePortalEndUserList, usePortalInvoicingPlatformList } from './portalHooks'
import { getData } from 'country-list'
import { usePaymentTerms } from 'src/hooks'

function OrderInfo({
  order,
  orderConf,
  confChanges,
  shippingAddresses,
  isCorrection = false,
  setView,
  setOrder,
  setShippingAddresses,
}: {
  order: portalOrderType
  orderConf: React.MutableRefObject<confidenceType>
  confChanges: React.MutableRefObject<confidenceType>
  shippingAddresses: (shipToType & { default: boolean; id: number })[]
  isCorrection?: boolean
  setOrder: React.Dispatch<React.SetStateAction<portalOrderType>>
  setView: React.Dispatch<React.SetStateAction<number>>
  setShippingAddresses: React.Dispatch<React.SetStateAction<(shipToType & { default: boolean; id: number })[]>>
}) {
  const invoicingPlatforms = usePortalInvoicingPlatformList()
  const divisionList = usePortalDivisionList()
  const endUserList = usePortalEndUserList()
  const paymentTerms = usePaymentTerms()

  const [licenseSameAsReceiver, setLicenseSameAsReceiver] = useState(false)
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(false)
  const [confirmed, setConfirmed] = useState({
    customerPO: false,
    awardDate: false,
    productType: false,
    shipToAttention: false,
    shipToCompany: false,
    shipToAddress1: false,
    shipToAddress2: false,
    shipToCity: false,
    shipToState: false,
    shipToZip: false,
    shipToCountry: false,
    billToAttention: false,
    billToCompany: false,
    billToAddress1: false,
    billToAddress2: order.PurchaseVehicle === 'ICPT UCOR',
    billToCity: order.PurchaseVehicle === 'ICPT UCOR',
    billToState: order.PurchaseVehicle === 'ICPT UCOR',
    billToZip: order.PurchaseVehicle === 'ICPT UCOR',
    billToCountry: order.PurchaseVehicle === 'ICPT UCOR',
    receiverContactName: false,
    receiverContactEmail: false,
    receiverContactPhone: false,
    deliveryInstructions: false,
    licenseContactName: false,
    licenseContactEmail: false,
    licenseContactPhone: false,
    govPaymentTerms: order.GovPaymentTerms === 'Credit Card',
    uidRequirement: order.PurchaseVehicle === 'ICPT UCOR',
    deliveryDueDate: false,
    eplRequirement: order.PurchaseVehicle === 'ICPT UCOR',
    invoicingPlatform: order.PurchaseVehicle === 'ICPT UCOR',
    securityRequirement: order.PurchaseVehicle === 'ICPT UCOR',
    division: false,
    endUserType: false,
  })

  const countryList = useMemo(() => {
    return getData().sort((a, b) => (a.code === 'US' ? -1 : b.code === 'US' ? 1 : a.name.localeCompare(b.name)))
  }, [])

  const onContinue = () => {
    setView(2)
  }

  type inputRowProps<T> = {
    label: string
    value: T | null
    checked: boolean
    onCheckboxChange: (checked: boolean) => void
    onChange: (value: T | null) => void
    confidence?: number | null
    textWidth?: string
    inputOnly?: boolean
    disabled?: boolean
    required?: boolean
    noCheckbox?: boolean
    tooltip?: React.ReactNode
  }

  const InputWrapper = useMemo(
    () =>
      function InputWrapper<T>({
        children,
        label,
        value,
        checked,
        onCheckboxChange,
        confidence,
        textWidth = '50%',
        inputOnly = false,
        disabled = false,
        required = false,
        noCheckbox = false,
        tooltip,
      }: inputRowProps<T> & { children: React.ReactNode }) {
        return (
          <div className="order-info-input-row">
            {!inputOnly && (
              <Typography variant="body2" sx={{ width: 'calc(100% - ' + textWidth + ')' }}>
                {label + (required ? ' *' : '')}
              </Typography>
            )}
            <div style={{ width: inputOnly ? '100%' : textWidth }}>
              {children}
              {tooltip}
              {confidence !== undefined && (
                <Typography>{confidence === null ? '-%' : `${(confidence * 100).toFixed(0)}%`}</Typography>
              )}
              {!noCheckbox && (
                <Checkbox
                  checked={checked}
                  disabled={disabled}
                  onChange={e => {
                    const isEmpty = value === null || (typeof value === 'string' && value === '')
                    if (e.target.checked && required && isEmpty) {
                      return
                    }
                    onCheckboxChange(e.target.checked)
                  }}
                />
              )}
            </div>
          </div>
        )
      },
    [],
  )

  const InputRow = useMemo(
    () =>
      function InputRow(props: inputRowProps<string> & { maxLength?: number }) {
        return (
          <InputWrapper {...props}>
            <TextField
              label={props.label}
              required={props.required}
              size="small"
              fullWidth
              value={props.value}
              onChange={e => props.onChange(e.target.value)}
              disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
              slotProps={props.maxLength ? { htmlInput: { maxLength: props.maxLength } } : undefined}
            />
          </InputWrapper>
        )
      },
    [],
  )

  const SelectInputRow = useMemo(
    () =>
      function SelectInputRow(props: inputRowProps<string> & { options: string[] }) {
        return (
          <InputWrapper {...props}>
            <TextField
              required={props.required}
              select
              label={props.label}
              size="small"
              fullWidth
              value={props.value}
              onChange={e => props.onChange(e.target.value)}
              disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
            >
              {props.options.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </InputWrapper>
        )
      },
    [],
  )

  const AutocompleteInputRow = useMemo(
    () =>
      function AutocompleteInputRow<T>(
        props: inputRowProps<T> & { options: T[]; getOptionLabel?: (option: T) => string },
      ) {
        return (
          <InputWrapper {...props}>
            <Autocomplete
              fullWidth
              size="small"
              options={props.options}
              getOptionLabel={props.getOptionLabel}
              onChange={(_, value) => props.onChange(value)}
              value={props.value}
              renderInput={params => <TextField {...params} label={props.label} required={props.required} />}
              disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
            />
          </InputWrapper>
        )
      },
    [],
  )

  const DateInputRow = useMemo(
    () =>
      function DateInputRow(props: inputRowProps<Dayjs>) {
        return (
          <InputWrapper {...props}>
            <DatePicker
              label={props.label + (props.required ? ' *' : '')}
              size="small"
              fullWidth
              value={props.value}
              onChange={value => props.onChange(value)}
              disabled={props.disabled || (props.noCheckbox ? false : props.checked)}
            />
          </InputWrapper>
        )
      },
    [],
  )

  useEffect(() => {
    if (!isCorrection) {
      setOrder(prev => ({
        ...prev,
        VendorPaymentTerms: prev.VendorPaymentTerms ?? 'Net 30',
        GovPaymentTerms: prev.GovPaymentTerms ?? 'Net 30',
        DPASRating: prev.DPASRating ?? 'NONE',
        BillToAddressCountry: prev.BillToAddressCountry ?? 'United States of America (the)',
      }))
    }

    if (order.PurchaseVehicle === 'ICPT UCOR' && !isCorrection) {
      setOrder(prev => ({
        ...prev,
        GrossMargin: 0,
        ProductType: 'Hardware',
        BillToCompany: 'UCOR',
        BillToAddress1: 'invoices.ap@orcc.doe.gov',
        ShipToAttention: 'Chris Carpenter',
        ShipToAddress1: 'T3 UNITED CLEANUP OAK RIDGE LLC',
        ShipToAddress2: 'IT Deliveries 237 Brisbane Road T3',
        ShipToAddressCity: 'Oak Ridge',
        ShipToAddressZip: '37830',
        ShipToAddressState: 'TN',
        ShipToAddressCountry: 'United States',
        DeliveryDueDate: dayjs().add(30, 'day').toDate(),
        AwardDate: dayjs().toDate(),
        GovPaymentTerms: 'Net 15',
      }))

      if (shippingAddresses.length === 1) {
        setShippingAddresses(
          prev =>
            [
              {
                ShipToAttention: prev[0].ShipToAttention ?? 'Chris Carpenter',
                ShipToCompany: prev[0].ShipToCompany ?? 'UCOR',
                ShipToAddress1: prev[0].ShipToAddress1 ?? 'T3 UNITED CLEANUP OAK RIDGE LLC',
                ShipToAddress2: prev[0].ShipToAddress2 ?? 'IT Deliveries 237 Brisbane Road T3',
                ShipToAddressCity: prev[0].ShipToAddressCity ?? 'Oak Ridge',
                ShipToAddressZip: prev[0].ShipToAddressZip ?? '37830',
                ShipToAddressState: prev[0].ShipToAddressState ?? 'TN',
                ShipToAddressCountry: prev[0].ShipToAddressCountry ?? 'United States',
                ReceiverContactName: prev[0].ReceiverContactName ?? null,
                ReceiverContactEmail: prev[0].ReceiverContactEmail ?? null,
                ReceiverContactPhone: prev[0].ReceiverContactPhone ?? null,
                default: true,
                id: prev[0].id,
              },
            ] as (shipToType & { default: boolean; id: number })[],
        )
      }
    }
  }, [])

  return (
    <>
      <Alert severity="info" className="order-info-alert">
        Information must be confirmed using the checkboxes.
      </Alert>
      <div className="order-info-button-container">
        <Button variant="contained" onClick={() => setView(0)}>
          Previous
        </Button>
        <Button variant="contained" disabled={Object.values(confirmed).some(value => !value)} onClick={onContinue}>
          Next
        </Button>
      </div>
      <Accordion disableGutters defaultExpanded className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Customer Award Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="24px">
            <Grid size={4}>
              <InputRow
                label="Customer PO #"
                value={order.CustomerPO}
                onChange={value => {
                  setOrder(prev => ({ ...prev, CustomerPO: value }))
                  confChanges.current.CustomerPO = -1
                }}
                confidence={orderConf.current.CustomerPO}
                checked={confirmed.customerPO}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, customerPO: checked }))}
                inputOnly
                required
                maxLength={fieldLengths.orders.customerPO}
              />
            </Grid>
            <Grid size={4}>
              <DateInputRow
                required
                inputOnly
                label="Award Date"
                value={order.AwardDate ? dayjs(order.AwardDate) : null}
                onChange={value => {
                  setOrder(prev => ({ ...prev, AwardDate: value ? dayjs(value).toDate() : null }))
                  confChanges.current.AwardDate = -1
                }}
                checked={confirmed.awardDate}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, awardDate: checked }))}
                confidence={orderConf.current.AwardDate}
              />
            </Grid>
            <Grid size={4}>
              <SelectInputRow
                inputOnly
                required
                options={invoicingPlatforms}
                label="Invoicing Platform "
                value={order.InvoicingPlatform ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, InvoicingPlatform: value }))}
                checked={confirmed.invoicingPlatform}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, invoicingPlatform: checked }))}
              />
            </Grid>
            <Grid size={4}>
              <SelectInputRow
                inputOnly
                label="Customer Payment Terms"
                value={!!order.IsCreditCard ? 'Credit Card' : order.GovPaymentTerms ?? ''}
                onChange={value => {
                  setOrder(prev => ({ ...prev, GovPaymentTerms: value, IsCreditCard: value === 'Credit Card' }))
                  if (value === 'Credit Card') {
                    setConfirmed(prev => ({ ...prev, govPaymentTerms: true }))
                  }
                }}
                checked={confirmed.govPaymentTerms}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, govPaymentTerms: checked }))}
                confidence={orderConf.current.GovPaymentTerms}
                options={paymentTerms.map(term => term.name)}
                disabled={order.GovPaymentTerms === 'Credit Card'}
                noCheckbox={order.GovPaymentTerms === 'Credit Card'}
              />
            </Grid>
            <Grid size={4}>
              <DateInputRow
                inputOnly
                label="Delivery Due Date "
                value={order.DeliveryDueDate ? dayjs(order.DeliveryDueDate) : null}
                onChange={value => {
                  setOrder(prev => ({ ...prev, DeliveryDueDate: value ? dayjs(value).toDate() : null }))
                  confChanges.current.DeliveryDueDate = -1
                }}
                checked={confirmed.deliveryDueDate}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, deliveryDueDate: checked }))}
                confidence={orderConf.current.DeliveryDueDate}
                required
              />
            </Grid>
            <Grid size={4}>
              <SelectInputRow
                inputOnly
                label="Division"
                value={order.Division ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, Division: value }))}
                checked={confirmed.division}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, division: checked }))}
                options={divisionList}
              />
            </Grid>
            <Grid size={4}>
              <SelectInputRow
                inputOnly
                label="End User Type"
                value={order.EndUserType ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, EndUserType: value }))}
                checked={confirmed.endUserType}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, endUserType: checked }))}
                options={endUserList}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters defaultExpanded className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">IMPRES Quote Information</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="24px">
            <Grid size={4}>
              <SelectInputRow
                inputOnly
                required
                options={productTypes}
                label="Product Type"
                value={order.ProductType ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, ProductType: value }))}
                checked={confirmed.productType}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, productType: checked }))}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters defaultExpanded className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Security Requirements</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="24px">
            <Grid size={4}>
              <InputRow
                inputOnly
                label="UID Requirement"
                value={order.UIDRequirement}
                onChange={value => setOrder(prev => ({ ...prev, UIDRequirement: value }))}
                checked={confirmed.uidRequirement}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, uidRequirement: checked }))}
                maxLength={fieldLengths.orders.uidRequirement}
              />
            </Grid>
            <Grid size={4}>
              <InputRow
                inputOnly
                label="EPL Requirement"
                value={order.EPLRequirement}
                onChange={value => setOrder(prev => ({ ...prev, EPLRequirement: value }))}
                checked={confirmed.eplRequirement}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, eplRequirement: checked }))}
                maxLength={fieldLengths.orders.eplRequirement}
              />
            </Grid>
            <Grid size={4}>
              <SelectInputRow
                inputOnly
                label="Security Requirement"
                value={order.SecurityRequirement ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, SecurityRequirement: value }))}
                checked={confirmed.securityRequirement}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, securityRequirement: checked }))}
                confidence={orderConf.current.SecurityRequirement}
                options={securityRequirementOptions}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion disableGutters defaultExpanded className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <div className="order-info-shipping-billing-header">
            <Typography variant="h6">Shipping and Billing Details</Typography>
            <FormControlLabel
              control={<Checkbox onClick={e => e.stopPropagation()} />}
              label="Billing information is the same as default shipping information"
              checked={billingSameAsShipping}
              onChange={(_e, checked) => {
                setBillingSameAsShipping(checked)
                if (checked) {
                  setOrder(prev => ({
                    ...prev,
                    BillToAttention: prev.ShipToAttention,
                    BillToCompany: prev.ShipToCompany,
                    BillToAddress1: prev.ShipToAddress1,
                    BillToAddress2: prev.ShipToAddress2,
                    BillToAddressCity: prev.ShipToAddressCity,
                    BillToAddressState: prev.ShipToAddressState,
                    BillToAddressZip: prev.ShipToAddressZip,
                    BillToAddressCountry: prev.ShipToAddressCountry,
                  }))
                  setConfirmed(prev => ({
                    ...prev,
                    billToAttention: prev.shipToAttention,
                    billToCompany: prev.shipToCompany,
                    billToAddress1: prev.shipToAddress1,
                    billToAddress2: prev.shipToAddress2,
                    billToCity: prev.shipToCity,
                    billToState: prev.shipToState,
                    billToZip: prev.shipToZip,
                    billToCountry: prev.shipToCountry,
                  }))
                }
              }}
              slotProps={{ typography: { onClick: e => e.stopPropagation() } }}
            />
          </div>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="56px">
            <Grid size={6}>
              <Stack spacing="17px">
                <div className="order-info-shipping-billing-content-header">
                  <Typography variant="h6">Shipping</Typography>
                </div>
                {shippingAddresses
                  .sort((a, b) => a.id - b.id)
                  .map((address, index) => (
                    <Accordion
                      defaultExpanded
                      disableGutters
                      className="order-info-shipping-address-accordion"
                      key={address.id}
                    >
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography>Address {index + 1}</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <div className="order-info-shipping-address-header">
                          <FormControlLabel
                            control={<Radio />}
                            checked={address.default}
                            onChange={(_, checked) => {
                              setShippingAddresses(prev =>
                                prev.map(a => ({ ...a, default: a.id === address.id ? checked : false })),
                              )
                              setOrder(prev => ({
                                ...prev,
                                ShipToAttention: address.ShipToAttention,
                                ShipToCompany: address.ShipToCompany,
                                ShipToAddress1: address.ShipToAddress1,
                                ShipToAddress2: address.ShipToAddress2,
                                ShipToAddressCity: address.ShipToAddressCity,
                                ShipToAddressState: address.ShipToAddressState,
                                ShipToAddressZip: address.ShipToAddressZip,
                                ShipToAddressCountry: address.ShipToAddressCountry,
                              }))
                              setConfirmed(prev => ({
                                ...prev,
                                shipToAddress1: address.ShipToAddress1 ? prev.shipToAddress1 : false,
                              }))
                            }}
                            label="Set as default address"
                          />
                          <IconButton
                            onClick={() => setShippingAddresses(prev => prev.filter(a => a.id !== address.id))}
                            disabled={address.default}
                          >
                            <DeleteOutline />
                          </IconButton>
                        </div>
                        <InputRow
                          label="Attention "
                          value={address.ShipToAttention}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAttention: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAttention: value,
                                BillToAttention: billingSameAsShipping ? value : prev.BillToAttention,
                              }))
                            }
                          }}
                          checked={confirmed.shipToAttention}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToAttention: checked,
                              billToAttention: billingSameAsShipping ? checked : prev.billToAttention,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAttention}
                        />
                        <InputRow
                          label="Addressee (Company) "
                          value={address.ShipToCompany}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToCompany: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToCompany: value,
                                BillToCompany: billingSameAsShipping ? value : prev.BillToCompany,
                              }))
                            }
                          }}
                          checked={confirmed.shipToCompany}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToCompany: checked,
                              billToCompany: billingSameAsShipping ? checked : prev.billToCompany,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToCompany}
                        />
                        <InputRow
                          label="Address Line 1 "
                          value={address.ShipToAddress1}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddress1: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddress1: value,
                                BillToAddress1: billingSameAsShipping ? value : prev.BillToAddress1,
                              }))
                            }
                          }}
                          checked={confirmed.shipToAddress1}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToAddress1: checked,
                              billToAddress1: billingSameAsShipping ? checked : prev.billToAddress1,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddress1}
                        />
                        <InputRow
                          label="Address Line 2"
                          value={address.ShipToAddress2}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddress2: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddress2: value,
                                BillToAddress2: billingSameAsShipping ? value : prev.BillToAddress2,
                              }))
                            }
                          }}
                          checked={confirmed.shipToAddress2}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToAddress2: checked,
                              billToAddress2: billingSameAsShipping ? checked : prev.billToAddress2,
                            }))
                          }
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddress2}
                        />
                        <InputRow
                          label="City "
                          value={address.ShipToAddressCity}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddressCity: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddressCity: value,
                                BillToAddressCity: billingSameAsShipping ? value : prev.BillToAddressCity,
                              }))
                            }
                          }}
                          checked={confirmed.shipToCity}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToCity: checked,
                              billToCity: billingSameAsShipping ? checked : prev.billToCity,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddressCity}
                        />
                        <InputRow
                          label="State "
                          value={address.ShipToAddressState}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddressState: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddressState: value,
                                BillToAddressState: billingSameAsShipping ? value : prev.BillToAddressState,
                              }))
                            }
                          }}
                          checked={confirmed.shipToState}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToState: checked,
                              billToState: billingSameAsShipping ? checked : prev.billToState,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddressState}
                        />
                        <InputRow
                          label="Postal Code / Zip "
                          value={address.ShipToAddressZip}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddressZip: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddressZip: value,
                                BillToAddressZip: billingSameAsShipping ? value : prev.BillToAddressZip,
                              }))
                            }
                          }}
                          checked={confirmed.shipToZip}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToZip: checked,
                              billToZip: billingSameAsShipping ? checked : prev.billToZip,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.shipToAddressZip}
                        />
                        <AutocompleteInputRow<string>
                          options={countryList.map(({ name }) => name)}
                          label="Country"
                          value={address.ShipToAddressCountry ?? ''}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ShipToAddressCountry: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ShipToAddressCountry: value,
                                BillToAddressCountry: billingSameAsShipping ? value : prev.BillToAddressCountry,
                              }))
                            }
                          }}
                          checked={confirmed.shipToCountry}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              shipToCountry: checked,
                              billToCountry: billingSameAsShipping ? checked : prev.billToCountry,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                        />
                        <InputRow
                          label="Receiver Contact Name "
                          value={address.ReceiverContactName}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ReceiverContactName: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ReceiverContactName: value,
                                LicenseContactName: licenseSameAsReceiver ? value : prev.LicenseContactName,
                              }))
                            }
                          }}
                          checked={confirmed.receiverContactName}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              receiverContactName: checked,
                              licenseContactName: licenseSameAsReceiver ? checked : prev.licenseContactName,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.receiverContactName}
                        />
                        <InputRow
                          label="Receiver Contact Email "
                          value={address.ReceiverContactEmail}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ReceiverContactEmail: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ReceiverContactEmail: value,
                                LicenseContactEmail: licenseSameAsReceiver ? value : prev.LicenseContactEmail,
                              }))
                            }
                          }}
                          checked={confirmed.receiverContactEmail}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              receiverContactEmail: checked,
                              licenseContactEmail: licenseSameAsReceiver ? checked : prev.licenseContactEmail,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.receiverContactEmail}
                        />
                        <InputRow
                          label="Receiver Contact Phone "
                          value={address.ReceiverContactPhone}
                          onChange={value => {
                            setShippingAddresses(prev =>
                              prev.map(a => (a.id === address.id ? { ...a, ReceiverContactPhone: value } : a)),
                            )
                            if (address.default) {
                              setOrder(prev => ({
                                ...prev,
                                ReceiverContactPhone: value,
                                LicenseContactPhone: licenseSameAsReceiver ? value : prev.LicenseContactPhone,
                              }))
                            }
                          }}
                          checked={confirmed.receiverContactPhone}
                          onCheckboxChange={checked =>
                            setConfirmed(prev => ({
                              ...prev,
                              receiverContactPhone: checked,
                              licenseContactPhone: licenseSameAsReceiver ? checked : prev.licenseContactPhone,
                            }))
                          }
                          required
                          noCheckbox={!address.default}
                          maxLength={fieldLengths.orders.receiverContactPhone}
                        />
                      </AccordionDetails>
                    </Accordion>
                  ))}
                <div className="order-info-add-shipping-address">
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      setShippingAddresses(prev => [
                        ...prev,
                        {
                          id: prev.reduce((max, a) => Math.max(max, a.id), 0) + 1,
                          default: false,
                          ShipToAttention: null,
                          ShipToCompany: null,
                          ShipToAddress1: null,
                          ShipToAddress2: null,
                          ShipToAddressCity: null,
                          ShipToAddressState: null,
                          ShipToAddressZip: null,
                          ShipToAddressCountry: null,
                          ReceiverContactName: null,
                          ReceiverContactEmail: null,
                          ReceiverContactPhone: null,
                        },
                      ])
                    }
                    disabled={shippingAddresses.some(
                      a =>
                        !a.ShipToAttention ||
                        !a.ShipToCompany ||
                        !a.ShipToAddress1 ||
                        !a.ShipToAddressCity ||
                        !a.ShipToAddressState ||
                        !a.ShipToAddressZip ||
                        !a.ShipToAddressCountry ||
                        !a.ReceiverContactName ||
                        !a.ReceiverContactEmail ||
                        !a.ReceiverContactPhone,
                    )}
                  >
                    Add Address
                  </Button>
                </div>
                <FormControlLabel
                  control={<Checkbox />}
                  label="License POC same as Default Receiving POC"
                  checked={licenseSameAsReceiver}
                  onChange={(_e, checked) => {
                    setLicenseSameAsReceiver(checked)
                    if (checked) {
                      setOrder(prev => ({
                        ...prev,
                        LicenseContactName: prev.ReceiverContactName,
                        LicenseContactEmail: prev.ReceiverContactEmail,
                        LicenseContactPhone: prev.ReceiverContactPhone,
                      }))
                      setConfirmed(prev => ({
                        ...prev,
                        licenseContactName: prev.receiverContactName,
                        licenseContactEmail: prev.receiverContactEmail,
                        licenseContactPhone: prev.receiverContactPhone,
                      }))
                    }
                  }}
                />
              </Stack>
            </Grid>
            <Grid size={6}>
              <Stack spacing="17px">
                <div className="order-details-shipping-billing-header">
                  <Typography variant="h6">Billing</Typography>
                </div>
                <InputRow
                  label="Attention "
                  value={order.BillToAttention ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAttention: value }))}
                  checked={confirmed.billToAttention}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToAttention: checked }))}
                  disabled={billingSameAsShipping}
                  maxLength={fieldLengths.orders.billToAttention}
                />
                <InputRow
                  label="Addressee (Company) "
                  value={order.BillToCompany ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToCompany: value }))}
                  checked={confirmed.billToCompany}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToCompany: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToCompany}
                />
                <InputRow
                  label="Address Line 1 "
                  value={order.BillToAddress1 ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddress1: value }))}
                  checked={confirmed.billToAddress1}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToAddress1: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToAddress1}
                />
                <InputRow
                  label="Address Line 2"
                  value={order.BillToAddress2 ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddress2: value }))}
                  checked={confirmed.billToAddress2}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToAddress2: checked }))}
                  disabled={billingSameAsShipping}
                  maxLength={fieldLengths.orders.billToAddress2}
                />
                <InputRow
                  label="City "
                  value={order.BillToAddressCity ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddressCity: value }))}
                  checked={confirmed.billToCity}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToCity: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToAddressCity}
                />
                <InputRow
                  label="State "
                  value={order.BillToAddressState ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddressState: value }))}
                  checked={confirmed.billToState}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToState: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToAddressState}
                />
                <InputRow
                  label="Postal Code / Zip "
                  value={order.BillToAddressZip ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddressZip: value }))}
                  checked={confirmed.billToZip}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToZip: checked }))}
                  disabled={billingSameAsShipping}
                  required
                  maxLength={fieldLengths.orders.billToAddressZip}
                />
                <AutocompleteInputRow<string>
                  options={countryList.map(({ name }) => name)}
                  label="Country "
                  value={order.BillToAddressCountry ?? ''}
                  onChange={value => setOrder(prev => ({ ...prev, BillToAddressCountry: value }))}
                  checked={confirmed.billToCountry}
                  onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, billToCountry: checked }))}
                  disabled={billingSameAsShipping}
                  required
                />
              </Stack>
            </Grid>
          </Grid>
          <div className="order-info-delivery-instructions">
            <div>
              <Typography variant="body2">Delivery Instructions</Typography>
              <Checkbox
                checked={confirmed.deliveryInstructions}
                onChange={e => setConfirmed(prev => ({ ...prev, deliveryInstructions: e.target.checked }))}
              />
            </div>
            <TextField
              className="order-info-textarea"
              multiline
              label="Delivery Instructions"
              rows={3}
              fullWidth
              value={order.DeliveryInstructions ?? ''}
              onChange={e => setOrder(prev => ({ ...prev, DeliveryInstructions: e.target.value }))}
              disabled={confirmed.deliveryInstructions}
              slotProps={{ htmlInput: { maxLength: fieldLengths.orders.deliveryInstructions } }}
            />
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded disableGutters className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">License Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing="56px" className="order-details-license-details-container">
            <Grid size={4}>
              <InputRow
                label="License Contact Name"
                value={order.LicenseContactName ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, LicenseContactName: value }))}
                textWidth="70%"
                checked={confirmed.licenseContactName}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, licenseContactName: checked }))}
                disabled={licenseSameAsReceiver}
                maxLength={fieldLengths.orders.licenseContactName}
              />
            </Grid>
            <Grid size={4}>
              <InputRow
                label="License Contact Email"
                value={order.LicenseContactEmail ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, LicenseContactEmail: value }))}
                textWidth="70%"
                checked={confirmed.licenseContactEmail}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, licenseContactEmail: checked }))}
                disabled={licenseSameAsReceiver}
                maxLength={fieldLengths.orders.licenseContactEmail}
              />
            </Grid>
            <Grid size={4}>
              <InputRow
                label="License Contact Phone"
                value={order.LicenseContactPhone ?? ''}
                onChange={value => setOrder(prev => ({ ...prev, LicenseContactPhone: value }))}
                textWidth="70%"
                checked={confirmed.licenseContactPhone}
                onCheckboxChange={checked => setConfirmed(prev => ({ ...prev, licenseContactPhone: checked }))}
                disabled={licenseSameAsReceiver}
                maxLength={fieldLengths.orders.licenseContactPhone}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      <Accordion defaultExpanded disableGutters className="order-info-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="h6">Purchasing Details</Typography>
        </AccordionSummary>
        <AccordionDetails className="order-info-misc">
          <div className="order-info-special-instructions">
            <Typography variant="body2">Special Order / PO Instruction</Typography>
            <TextField
              className="order-info-textarea"
              multiline
              label="Special Order / PO Instruction"
              rows={3}
              fullWidth
              slotProps={{ htmlInput: { maxLength: fieldLengths.orders.specialInstructions } }}
              value={order.SpecialInstructions ?? ''}
              onChange={e => setOrder(prev => ({ ...prev, SpecialInstructions: e.target.value }))}
            />
          </div>
        </AccordionDetails>
      </Accordion>
      <div className="order-info-button-container">
        <Button variant="contained" onClick={() => setView(0)}>
          Previous
        </Button>
        <Button variant="contained" disabled={Object.values(confirmed).some(value => !value)} onClick={onContinue}>
          Next
        </Button>
      </div>
    </>
  )
}

export default OrderInfo
